/* global ResizeObserver */
import * as React from "react";
import { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { isShapeWithAlts } from "../../helpers/shapeWithAltsUtils";
import { getShapeProperties } from "../../helpers/shapeTypes";

const CellShape = (props) => {
  const shapeRef = useRef(null);

  const marginLeft = props.marginLeft ?? props.margin ?? 0;
  const marginLeftPerc = props.marginLeftPerc ?? props.marginPerc ?? 0;
  const ml = `calc(${marginLeftPerc}% + ${marginLeft}px)`;
  const marginRight = props.marginRight ?? props.margin ?? 0;
  const marginRightPerc = props.marginRightPerc ?? props.marginPerc ?? 0;
  const mr = `calc(${marginRightPerc}% + ${marginRight}px)`;
  const width = `calc(100% - ${marginLeft + marginRight}px - ${marginLeftPerc + marginRightPerc}%)`;

  const marginTop = props.marginTop ?? props.margin ?? 0;
  const marginTopPerc = props.marginTopPerc ?? props.marginPerc ?? 0;
  const mt = `calc(${marginTopPerc}% + ${marginTop}px)`;
  const marginBottom = props.marginBottom ?? props.margin ?? 0;
  const marginBottomPerc = props.marginBottomPerc ?? props.marginPerc ?? 0;
  const mb = `calc(${marginBottomPerc}% + ${marginBottom}px)`;
  const height = `calc(100% - ${marginTop + marginBottom}px - ${marginTopPerc + marginBottomPerc}%)`;

  // console.log("Shape margin calcs", {
  //   width,
  //   height,
  //   ml,
  //   mr,
  //   mt,
  //   mb,
  //   style: props,
  // });

  const [calculatedRadius, setCalculatedRadius] = useState("");

  useEffect(() => {
    const updateRadius = () => {
      if (shapeRef.current) {
        const width = shapeRef.current?.offsetWidth ?? 0;
        const height = shapeRef.current?.offsetHeight ?? 0;
        const minDimension = Math.min(width, height);
        const pptConstant = 0.15;
        const radius = `${pptConstant * minDimension}px`;
        setCalculatedRadius(radius);
      }
    };

    updateRadius();

    if (typeof ResizeObserver !== "undefined" && shapeRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        updateRadius();
      });
      resizeObserver.observe(shapeRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }

    return undefined;
  }, []);

  // TODO: special rotation with alternative shapes requires documentation
  const needsSpecialRotation =
    isShapeWithAlts(props.type) && (props.rotation === 90 || props.rotation === 270 || props.rotation === -90);

  const cssRotation = needsSpecialRotation ? props.rotation - 90 : props.rotation;

  const transform = `rotate(${cssRotation}deg) scale(${props.flipX ? -1 : 1}, ${props.flipY ? -1 : 1})`;
  const transformOrigin = "50% 50%";

  const shadow = props.shadow
    ? (() => {
        const angle = 45;
        const distance = 3;
        const blur = 4;
        const transparency = props.shadow;

        const angleRad = (angle * Math.PI) / 180;
        const offsetX = Math.cos(angleRad) * distance;
        const offsetY = Math.sin(angleRad) * distance;

        return `drop-shadow(${offsetX}px ${offsetY}px ${blur}px rgba(0, 0, 0, ${transparency}))`;
      })()
    : undefined;

  const strokeDashArray =
    props.outline === "dashed"
      ? props.outlineWeight
        ? props.outlineWeight * 3
        : 4
      : props.outline === "dotted"
        ? (props.outlineWeight ?? 1)
        : undefined;

  const shapeProperties = getShapeProperties(props.type, needsSpecialRotation);
  const id = shapeProperties?.id + props.cellId;

  return (
    <div
      ref={shapeRef}
      style={{
        position: "absolute",
        zIndex: props.zIndex,
        left: ml,
        top: mt,
        right: mr,
        bottom: mb,
        width,
        height,
        background: props.type !== "RoundedRectangle" ? undefined : props.color,
        borderRadius: props.type === "RoundedRectangle" && props.radius !== 0 ? calculatedRadius : 0,
        borderWidth: props.type === "RoundedRectangle" ? props.outlineWeight : undefined,
        borderColor: props.type === "RoundedRectangle" ? props.outlineColor : undefined,
        borderStyle: props.type === "RoundedRectangle" ? props.outline : undefined,
        boxSizing: "border-box",
        overflow: props.shadow ? "visible" : "clip",
        filter: props.type === "RoundedRectangle" ? shadow : undefined,
      }}
    >
      {props.type !== "RoundedRectangle" && shapeProperties ? (
        <svg
          className={`shape_${props.cellId}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox={shapeProperties.viewBox}
          width={shapeProperties.width}
          height={shapeProperties.height}
          preserveAspectRatio={shapeProperties.preserveAspectRatio}
          style={{
            width: "100%",
            height: "100%",
            transform: transform,
            transformOrigin: transformOrigin,
            filter: shadow,
          }}
        >
          <defs>
            <style>
              {`
              #${id} {
                fill: ${props.color};
                stroke: ${props.outline === "none" ? "unset" : (props.outlineColor ?? "black")};
                stroke-width: ${2 * (props.outlineWeight ?? 0)}px;
                stroke-dasharray: ${strokeDashArray};
                shape-rendering: auto;
              }`}
            </style>
          </defs>
          {props.type === "Circle" ? (
            <circle
              id={id}
              cx={shapeProperties.cx}
              cy={shapeProperties.cy}
              r={shapeProperties.r}
              clipPath={`url(#${props.cellId})`}
            />
          ) : props.type === "ArrowCommon" ? (
            <polygon
              id={id}
              points={shapeProperties.d}
              clipPath={`url(#${props.cellId})`}
              shapeRendering="crispEdges"
            />
          ) : props.type === "RoundedThinRectangle" ? (
            <rect
              id={id}
              x={shapeProperties.x}
              y={shapeProperties.y}
              width={shapeProperties.rectWidth}
              height={shapeProperties.rectHeight}
              rx={shapeProperties.rx}
              ry={shapeProperties.ry}
              clipPath={`url(#${props.cellId})`}
            />
          ) : props.type === "Oval" ? (
            <ellipse
              id={id}
              cx={shapeProperties.cx}
              cy={shapeProperties.cy}
              rx={shapeProperties.rx}
              ry={shapeProperties.ry}
              clipPath={`url(#${props.cellId})`}
            />
          ) : props.type === "ArrowThin" ||
            props.type === "ArrowFull" ||
            props.type === "ArrowWithoutTail" ||
            props.type === "Quarter" ||
            props.type === "CurvedArrow" ||
            props.type === "Triangle" ||
            props.type === "Trapeze1" ||
            props.type === "Trapeze2" ||
            props.type === "Trapeze3" ||
            props.type === "LongTriangle" ? (
            <path id={id} d={shapeProperties.d} clipPath={`url(#${props.cellId})`} />
          ) : (
            <></>
          )}
          <clipPath id={props.cellId}>
            <use xlinkHref={`#${id}`} />
          </clipPath>
        </svg>
      ) : (
        <></>
      )}

      <div
        style={{
          position: "absolute",
          inset: props.type === "Circle" ? "15%" : 0,
          right: props.type === "ArrowFull" || props.type === "Circle" ? "15%" : 0,
          overflow: props.type === "Circle" ? "clip" : undefined,
        }}
      >
        {props.children}
      </div>
    </div>
  );
};

CellShape.propTypes = {
  type: PropTypes.string,
  outline: PropTypes.string,
  color: PropTypes.string,
  outlineWeight: PropTypes.number,
  outlineColor: PropTypes.string,
  radius: PropTypes.any,
  margin: PropTypes.number,
  marginPerc: PropTypes.number,
  marginLeft: PropTypes.number,
  marginRight: PropTypes.number,
  marginTop: PropTypes.number,
  marginBottom: PropTypes.number,
  marginLeftPerc: PropTypes.number,
  marginRightPerc: PropTypes.number,
  marginTopPerc: PropTypes.number,
  marginBottomPerc: PropTypes.number,
  zIndex: PropTypes.number,
  rotation: PropTypes.number,
  flipX: PropTypes.bool,
  flipY: PropTypes.bool,
  shadow: PropTypes.number,
  children: PropTypes.element,
  cellId: PropTypes.string,
};

export default CellShape;
